/**
 * Labstep
 *
 * @module prosemirror/extensions/slash/utils/sort-elements
 * @desc Utility functions for sorting menu elements by section
 */

import { MenuCommandsSection } from 'labstep-web/prosemirror/components/Menu/Commands';
import { MENU_COMMANDS_ELEMENTS } from 'labstep-web/prosemirror/components/Menu/Commands/elements';
import type { IProseMirrorCommandsElement } from 'labstep-web/prosemirror/extensions/slash/types';

/**
 * Sorts an array of element IDs by their section order
 * @param elements Array of element IDs
 * @returns Array of element IDs sorted by section order
 */
export const sortElementsBySection = (
  elements: IProseMirrorCommandsElement[],
): IProseMirrorCommandsElement[] => {
  if (!elements || elements.length === 0) {
    return elements;
  }

  // Create a map of element ID to element definition for quick lookup
  const elementMap = new Map();
  MENU_COMMANDS_ELEMENTS.forEach((el) => {
    elementMap.set(el.id, el);
  });

  // Define section order
  const sectionOrder = [
    MenuCommandsSection.Common,
    MenuCommandsSection.Tables,
    MenuCommandsSection.InputFields,
    MenuCommandsSection.OutputFields,
    MenuCommandsSection.DomainTools,
    MenuCommandsSection.Code,
  ];

  // Group elements by section
  const groups: { [key: string]: IProseMirrorCommandsElement[] } = {};
  elements.forEach((elementId) => {
    const element = elementMap.get(elementId);
    const section = element?.section || MenuCommandsSection.Common;
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(elementId);
  });

  // Flatten in section order
  const sortedElements: IProseMirrorCommandsElement[] = [];
  sectionOrder.forEach((sectionKey) => {
    if (groups[sectionKey]) {
      sortedElements.push(...groups[sectionKey]);
    }
  });

  return sortedElements;
};
